import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CalendarClock, CheckCircle2, MessageCircle, ArrowLeft } from 'lucide-react';
import { VerifyField } from '../components/VerifyField';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WA = 'https://wa.me/447985291097?text=' + encodeURIComponent('Hi Move Van Ltd — I need help rescheduling my booking.');

export default function Reschedule() {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [data, setData] = useState(null);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState('');
  const [verify, setVerify] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/estimate/${id}/public`);
        if (cancelled) return;
        setData(data);
        if (data.booking) {
          setDate(data.booking.date || '');
          setTime(data.booking.time || '');
        }
      } catch { if (!cancelled) setNotFound(true); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setErr('');
    setSubmitting(true);
    try {
      await axios.post(`${API}/estimate/${id}/reschedule`, { date, time, verify: verify.trim() });
      setDone({ date, time });
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Something went wrong. Please WhatsApp us.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-[color:var(--mv-orange)]" /></div>;
  }
  if (notFound) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <h1 className="font-display text-2xl font-extrabold text-[color:var(--mv-navy)]">Booking not found</h1>
          <p className="text-slate-600 mt-2 text-sm">That link looks invalid or has expired.</p>
          <a href={WA} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-4 bg-[#25D366] text-white font-bold px-4 py-2 rounded-md"><MessageCircle className="w-4 h-4" /> WhatsApp us</a>
        </div>
      </div>
    );
  }
  if (data?.cancelled_at) {
    return (
      <div className="min-h-[60vh] bg-slate-50 py-16 px-4">
        <div className="max-w-lg mx-auto bg-white rounded-xl shadow-sm border border-slate-200 p-6 text-center">
          <h1 className="font-display text-2xl font-extrabold text-[color:var(--mv-navy)]">This booking was cancelled</h1>
          <p className="text-slate-600 mt-2 text-sm">We can't reschedule a cancelled booking. If you'd like a new quote, please <Link to="/" className="text-[color:var(--mv-orange)] underline">start again</Link>.</p>
        </div>
      </div>
    );
  }
  if (done) {
    return (
      <div className="min-h-[60vh] bg-slate-50 py-16 px-4" data-testid="reschedule-done">
        <div className="max-w-lg mx-auto bg-white rounded-xl shadow-sm border border-slate-200 p-6 text-center">
          <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-6 h-6 text-emerald-600" />
          </div>
          <h1 className="font-display text-2xl font-extrabold text-[color:var(--mv-navy)] mt-3">Booking #{data?.ref} rescheduled</h1>
          <p className="text-slate-600 mt-2 text-sm">We've moved your booking to <strong>{done.date}</strong> at <strong>{done.time}</strong>. Your deposit stays put — see you then.</p>
          <div className="mt-5 flex items-center justify-center gap-2 flex-wrap">
            <a href={WA} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] text-white font-bold px-4 py-2 rounded-md text-sm"><MessageCircle className="w-4 h-4" /> WhatsApp us</a>
            <Link to="/" className="inline-flex items-center gap-2 border border-slate-200 text-[color:var(--mv-navy)] font-bold px-4 py-2 rounded-md text-sm">Back to home</Link>
          </div>
        </div>
      </div>
    );
  }

  const minDate = new Date().toISOString().slice(0, 10);

  return (
    <div className="min-h-[60vh] bg-slate-50 py-16 px-4">
      <form onSubmit={submit} className="max-w-lg mx-auto bg-white rounded-xl shadow-sm border border-slate-200 p-6" data-testid="reschedule-form">
        <div className="flex items-center gap-2 text-xs text-slate-500 mb-3">
          <Link to={`/cancel/${id}`} className="inline-flex items-center gap-1 hover:text-[color:var(--mv-orange)]"><ArrowLeft className="w-3 h-3" />Changed your mind? Cancel instead</Link>
        </div>
        <h1 className="font-display text-2xl font-extrabold text-[color:var(--mv-navy)]">Reschedule booking #{data?.ref}</h1>
        <p className="text-slate-600 mt-2 text-sm">
          Pick a new date and time. Your <strong>£{data?.deposit}</strong> deposit stays put.
        </p>
        {data?.booking && (
          <p className="text-slate-500 mt-3 text-xs">Currently booked for <strong>{data.booking.date}</strong> at <strong>{data.booking.time}</strong>.</p>
        )}

        <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mt-5 mb-1">New date</label>
        <input type="date" min={minDate} value={date} onChange={(e) => setDate(e.target.value)} required className="w-full h-11 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="reschedule-date" />

        <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mt-4 mb-1">New time</label>
        <input type="time" min="06:00" max="23:00" step="900" value={time} onChange={(e) => setTime(e.target.value)} required className="w-full h-11 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="reschedule-time" />

        <VerifyField value={verify} onChange={setVerify} testid="reschedule-verify" />
        {err && <div data-testid="reschedule-error" className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}

        <div className="mt-5 flex items-center gap-2 justify-end flex-wrap">
          <a href={WA} target="_blank" rel="noreferrer" className="h-11 px-4 rounded-md text-sm font-bold text-slate-600 hover:text-slate-900 inline-flex items-center gap-2">Message us instead</a>
          <button type="submit" disabled={submitting || !date || !time || !verify.trim()} className="h-11 px-5 rounded-md bg-[color:var(--mv-navy)] hover:bg-[#13315c] disabled:opacity-60 text-white text-sm font-bold inline-flex items-center gap-2" data-testid="reschedule-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarClock className="w-4 h-4" />} Confirm new date
          </button>
        </div>
      </form>
    </div>
  );
}
